/* =============================================================
   useStudentTemplate.js — Resolve the process template for a student
   -------------------------------------------------------------
   Used by the student portal (StudentDashboard, StudentDocuments,
   StudentNotificationBell) to find which process template applies
   to the current student, then pass its ID to useProcessConfig.

   Priority:
     1. student.process_template_id (manual admin override)
     2. Template matching degree_level + category
     3. Default template for this university + type
     4. First template for this university + type

   Returns: { templateId, loading }
   ============================================================= */

import { useState, useEffect } from 'react';
import { supabase }              from '../lib/supabase';
import { resolveProcessTemplate } from '../lib/studentCategory';

// Module-level cache: key = `${universityId}:${type}` → templates[]
const _templateCache = new Map();


export function useStudentTemplate(student) {
  const universityId = student?.university_id ?? null;
  const type         = student?.type          ?? null;
  const cacheKey     = universityId && type ? `${universityId}:${type}` : null;

  const _cached = cacheKey ? _templateCache.get(cacheKey) : undefined;

  const [templates, setTemplates] = useState(_cached ?? []);
  const [loading,   setLoading]   = useState(!student || _cached === undefined);

  useEffect(() => {
    if (!universityId || !type) { setLoading(false); return; }

    if (_templateCache.has(cacheKey)) setLoading(false);
    else setLoading(true);

    (async () => {
      const { data } = await supabase
        .from('university_process_templates')
        .select('id, semester_id, match_degree, match_categories, is_default')
        .eq('university_id', universityId)
        .eq('type', type)
        .order('created_at');

      const result = data ?? [];
      setTemplates(result);
      if (cacheKey) _templateCache.set(cacheKey, result);
      setLoading(false);
    })();
  }, [universityId, type, cacheKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // semester_id on templates + student.semester_id → resolveProcessTemplate does the filtering
  const resolved   = student ? resolveProcessTemplate(templates, student) : null;
  const templateId = resolved?.id ?? null;

  return { templateId, loading };
}
